import React, { useMemo } from 'react';

import type { ActionContract, ActionInputField } from 'model/actions';
import type { InputBinding } from 'model/workflow';
import InputFieldRenderer from './InputFieldRenderer';
import Styled from './InputsSection.styled';

const {
  StyledSection,
  StyledSectionLabel,
  StyledMuted,
  StyledRequiredHint,
  StyledRequiredMark,
  StyledAddInputWrapper,
  StyledAddInputButton,
} = Styled;

interface OwnProps {
  /** The action's declared input contract (null when contract is missing). */
  contract: ActionContract | null;
  /** The current bindings keyed by input name. */
  inputs: Record<string, InputBinding>;
  /** Step ids that may appear in the source-step select. */
  availableStepIds: string[];
  /** Resolve output names contributed by a source step. */
  resolveStepOutputs: (stepId: string) => string[];
  /** Edit / replace the binding for a given input name. */
  onBindingChange: (inputName: string, binding: InputBinding) => void;
  /** Remove a non-contract ("extra") input. */
  onRemoveExtraInput: (inputName: string) => void;
  /** Append a new "NewInput<n>" StepOutput-kind binding. */
  onAddInput: () => void;
}

const SYNTHETIC_INPUT_FIELD_BASE: Omit<ActionInputField, 'name'> = {
  type: 'any',
  required: false,
  allowedSources: ['StepOutput', 'ProcessingJobInput', 'Constant'],
};

const InputsSection = ({
  contract,
  inputs,
  availableStepIds,
  resolveStepOutputs,
  onBindingChange,
  onRemoveExtraInput,
  onAddInput,
}: OwnProps) => {
  const contractInputNames = useMemo<Set<string>>(
    () => new Set(contract?.inputs.map((f) => f.name) ?? []),
    [contract],
  );
  const extraInputNames = useMemo<string[]>(
    () => Object.keys(inputs).filter((name) => !contractInputNames.has(name)),
    [inputs, contractInputNames],
  );
  const hasRequiredInputs = useMemo(
    () => contract?.inputs.some((f) => f.required) ?? false,
    [contract],
  );
  // Synthesised field object per extra input, memoised so the `field`
  // prop reference stays stable across renders. Without this, React.memo
  // on InputFieldRenderer cannot short-circuit re-renders of extra inputs.
  const syntheticFieldByName = useMemo<Record<string, ActionInputField>>(() => {
    const result: Record<string, ActionInputField> = {};
    extraInputNames.forEach((name) => {
      result[name] = { ...SYNTHETIC_INPUT_FIELD_BASE, name };
    });
    return result;
  }, [extraInputNames]);
  const hasContractInputs = !!contract && contract.inputs.length > 0;
  const hasAnyInputs = hasContractInputs || extraInputNames.length > 0;

  return (
    <StyledSection>
      <StyledSectionLabel>Inputs</StyledSectionLabel>
      {!hasAnyInputs && <StyledMuted>No inputs for this action.</StyledMuted>}
      {contract?.inputs.map((field) => (
        <InputFieldRenderer
          key={field.name}
          field={field}
          binding={inputs[field.name]}
          availableStepIds={availableStepIds}
          resolveStepOutputs={resolveStepOutputs}
          onBindingChange={onBindingChange}
        />
      ))}
      {extraInputNames.map((inputName) => (
        <InputFieldRenderer
          key={inputName}
          field={syntheticFieldByName[inputName]}
          binding={inputs[inputName]}
          availableStepIds={availableStepIds}
          resolveStepOutputs={resolveStepOutputs}
          onBindingChange={onBindingChange}
          onRemove={onRemoveExtraInput}
        />
      ))}
      {hasRequiredInputs && (
        <StyledRequiredHint>
          <StyledRequiredMark>*</StyledRequiredMark> Required field
        </StyledRequiredHint>
      )}
      <StyledAddInputWrapper>
        <StyledAddInputButton type='button' onClick={onAddInput} className='btn-ghost btn-sm'>
          + Add input
        </StyledAddInputButton>
      </StyledAddInputWrapper>
    </StyledSection>
  );
};

export default React.memo(InputsSection);
